import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Play, Pause, RotateCcw, ChevronUp, ChevronDown, Gauge, Maximize2, Minimize2, AlignLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { ReadingSurface } from "@/components/reader/ReadingSurface";
import { ReaderSettings } from "@/components/reader/ReaderSettings";
import { HighlightsOverview } from "@/components/reader/HighlightsOverview";
import { useReaderSettings } from "@/components/reader/readerPrefs";
import { useSmoothFollow, prefersReducedMotion } from "@/components/reader/readerScroll";
import { buildDocModel, buildHighlightIndex, DEFAULT_LAYERS, expandHighlightSpan } from "@/components/reader/readingEngine";
import { SelectionExplainLayer } from "@/components/reader/useSelectionExplain";

const PREFS_KEY = "reading_os_prefs";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}; } catch (e) { return {}; } };
const savePref = (patch) => { try { localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...patch })); } catch (e) {} };

/**
 * Focus Reading — a calm, distraction-free layout. The ACTIVE SENTENCE stays in
 * full colour while the rest of the text is gently dimmed (not too strong), and
 * it auto-advances sentence by sentence with continuous, jump-free scrolling.
 * No karaoke word-trail here — the emphasis is deep comprehension + concentration.
 */
export const FocusStage = ({
    text = "",
    total = 0,
    initialIndex = 0,
    seekTo,
    onProgress,
    onSession,
    onComplete,
    highlights,
    onExplain,
}) => {
    const { t } = useTranslation();
    const rs = useReaderSettings();
    const model = useMemo(() => buildDocModel(text), [text]);
    const sentences = model.sentences;
    const count = sentences.length;
    const ratio = total > 0 ? total / model.totalTokens : 1;

    const initialSentence = useMemo(() => {
        const tok = Math.round((total > 0 ? initialIndex / total : 0) * model.totalTokens);
        let idx = 0;
        for (let i = 0; i < count; i++) { if (sentences[i].tokenStart <= tok) idx = i; else break; }
        return idx;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [active, setActive] = useState(initialSentence);
    const [playing, setPlaying] = useState(false);
    const [wpm, setWpm] = useState(() => loadPrefs().wpm || 350);
    const [dim, setDim] = useState(() => loadPrefs().focusDim ?? true);
    const [fullscreen, setFullscreen] = useState(false);

    const containerRef = useRef(null);
    const surfaceRef = useRef(null);
    const timerRef = useRef(null);
    const activeRef = useRef(active);
    const sentencesRef = useRef(sentences);
    const immediateScrollRef = useRef(false);
    const sessionRef = useRef({ startTime: null, startWord: 0 });

    const reducedMotion = useMemo(() => prefersReducedMotion(), []);
    const { follow, stop } = useSmoothFollow(containerRef, { reducedMotion });

    useEffect(() => { activeRef.current = active; }, [active]);
    useEffect(() => { sentencesRef.current = sentences; }, [sentences]);

    const enableHighlights = !!(highlights?.enabled && highlights?.data);
    const highlightIndex = useMemo(() => {
        if (!enableHighlights) return null;
        return buildHighlightIndex(model, highlights.data, highlights.layers || DEFAULT_LAYERS);
    }, [enableHighlights, model, highlights?.data, highlights?.layers]);

    const applyActive = useCallback((scroll) => {
        const surf = surfaceRef.current;
        if (!surf) return;
        surf.querySelectorAll(".rg-active").forEach((n) => { n.classList.remove("rg-active"); n.removeAttribute("data-testid"); });
        const s = sentencesRef.current[activeRef.current];
        if (!s) return;
        let first = null;
        for (let i = s.tokenStart; i <= s.tokenEnd; i++) {
            const n = surf.querySelector(`[data-tok="${i}"]`);
            if (n) { n.classList.add("rg-active"); if (!first) first = n; }
        }
        if (first) {
            first.setAttribute("data-testid", "focus-active-line");
            if (scroll && rs.autoScroll) follow(first, { immediate: immediateScrollRef.current });
        }
        immediateScrollRef.current = false;
    }, [follow, rs.autoScroll]);

    useEffect(() => { applyActive(true); }, [active, applyActive]);
    useEffect(() => { applyActive(false); }, [highlightIndex, dim, rs.surface.anchorMode, applyActive]);

    useEffect(() => { savePref({ wpm, focusDim: dim }); }, [wpm, dim]);

    // External seek.
    useEffect(() => {
        if (!seekTo || typeof seekTo.index !== "number" || !count) return;
        setPlaying(false);
        immediateScrollRef.current = true;
        const tok = Math.round((total > 0 ? seekTo.index / total : 0) * model.totalTokens);
        let idx = 0;
        for (let i = 0; i < count; i++) { if (sentences[i].tokenStart <= tok) idx = i; else break; }
        setActive(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seekTo?.ts]);

    useEffect(() => {
        const s = sentences[active];
        if (!s) return;
        const idx = Math.round(s.tokenStart * ratio);
        const pct = total ? (idx / total) * 100 : 0;
        onProgress?.(idx, pct);
    }, [active, sentences, ratio, total, onProgress]);

    const flushSession = useCallback(() => {
        const ses = sessionRef.current;
        if (ses.startTime == null) return;
        const dur = (Date.now() - ses.startTime) / 1000;
        const curWord = sentencesRef.current[activeRef.current]?.tokenStart ?? ses.startWord;
        const wr = Math.max(0, Math.round((curWord - ses.startWord) * ratio));
        ses.startTime = null;
        if (dur > 1 && wr > 0) onSession?.({ wordsRead: wr, durationSec: dur, wpm, mode: "focus" });
    }, [onSession, wpm, ratio]);

    useEffect(() => {
        if (!playing) return;
        if (active >= count - 1) { setPlaying(false); onComplete?.(); return; }
        const s = sentences[active];
        const words = Math.max(1, s?.wordCount || 1);
        const delay = Math.max(550, (words / Math.max(60, wpm)) * 60 * 1000);
        timerRef.current = setTimeout(() => setActive((a) => Math.min(count - 1, a + 1)), delay);
        return () => clearTimeout(timerRef.current);
    }, [playing, active, wpm, count, sentences, onComplete]);

    useEffect(() => {
        if (playing) sessionRef.current = { startTime: Date.now(), startWord: sentences[active]?.tokenStart ?? 0 };
        else { flushSession(); stop(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing]);
    useEffect(() => () => flushSession(), [flushSession]);

    const togglePlay = useCallback(() => { setActive((a) => (a >= count - 1 ? 0 : a)); setPlaying((p) => !p); }, [count]);
    const prev = useCallback(() => setActive((a) => Math.max(0, a - 1)), []);
    const next = useCallback(() => setActive((a) => Math.min(count - 1, a + 1)), [count]);
    const restart = useCallback(() => { setActive(0); setPlaying(false); stop(); }, [stop]);
    const onTokenClick = useCallback((i) => {
        setPlaying(false);
        const idx = sentencesRef.current.findIndex((s) => i >= s.tokenStart && i <= s.tokenEnd);
        if (idx >= 0) setActive(idx);
    }, []);

    // Jump to the sentence containing a token (Smart Highlights overview).
    const jumpToToken = useCallback((tok) => {
        setPlaying(false);
        immediateScrollRef.current = true;
        const arr = sentencesRef.current;
        let idx = 0;
        for (let i = 0; i < arr.length; i++) { if (arr[i].tokenStart <= tok) idx = i; else break; }
        setActive(idx);
    }, []);

    const explainToken = useCallback((tokenIndex, e) => {
        if (!onExplain) return;
        const target = expandHighlightSpan(model, highlightIndex, tokenIndex);
        if (!target?.term) return;
        setPlaying(false);
        onExplain(target.term, target.context, e.currentTarget.getBoundingClientRect(), Math.round(tokenIndex * ratio));
    }, [onExplain, model, highlightIndex, ratio]);

    useEffect(() => {
        const onKey = (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
            if (e.code === "Space") { e.preventDefault(); togglePlay(); }
            else if (e.code === "ArrowUp") { e.preventDefault(); prev(); }
            else if (e.code === "ArrowDown") { e.preventDefault(); next(); }
            else if (e.key === "f" || e.key === "F") setFullscreen((v) => !v);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [togglePlay, prev, next]);

    const pct = count ? Math.round(((active + 1) / count) * 100) : 0;

    return (
        <div className={cn("h-full", fullscreen ? "fixed inset-0 z-50 bg-surface-0 p-3 sm:p-6" : "")} data-testid="focus-stage">
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-surface-1">
                <div className="relative z-10 flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mono-label text-t3"><AlignLeft className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> {t("reader.modes.focus.label")}</div>
                        <p className="mt-0.5 hidden text-xs text-t4 sm:block">{t("rset.focusDesc")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <ReaderSettings settings={rs} hl={highlights} focus={{ dim, setDim }} />
                        {enableHighlights && highlights?.data && (
                            <HighlightsOverview
                                model={model}
                                data={highlights.data}
                                layers={highlights.layers}
                                ratio={ratio}
                                onJump={jumpToToken}
                                onExplain={onExplain}
                            />
                        )}
                        <Button data-testid="focus-fullscreen-toggle" variant="ghost" size="icon" className="text-t3" onClick={() => setFullscreen((v) => !v)}>
                            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>

                <div
                    ref={containerRef}
                    className={cn("rg-reader relative flex-1 overflow-y-auto px-6 py-10 sm:px-10", `rg-theme-${rs.readerTheme}`)}
                    data-testid="focus-scroll-container"
                >
                    <ReadingSurface
                        model={model}
                        surfaceRef={surfaceRef}
                        highlightIndex={highlightIndex}
                        enableHighlights={enableHighlights}
                        dim={dim}
                        onTokenClick={onTokenClick}
                        onExplain={onExplain ? explainToken : undefined}
                        anchorMode={rs.surface.anchorMode}
                        anchorIntensity={rs.surface.anchorIntensity}
                        fontFamily={rs.surface.fontFamily}
                        fontSize={rs.surface.fontSize}
                        maxWidth={rs.surface.maxWidth}
                        lineHeight={rs.surface.lineHeight}
                        textAlign={rs.surface.textAlign}
                        testid="focus-surface"
                    />
                    {onExplain && <SelectionExplainLayer containerRef={containerRef} onExplain={onExplain} ratio={ratio} />}
                </div>

                <div className="relative z-10 border-t border-border bg-surface-1/80 px-4 py-3 backdrop-blur-md">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5">
                            <Button data-testid="focus-restart-button" variant="ghost" size="icon" className="text-t3" onClick={restart}><RotateCcw className="h-4 w-4" /></Button>
                            <Button data-testid="focus-prev-button" variant="ghost" size="icon" className="text-t2" onClick={prev}><ChevronUp className="h-5 w-5" /></Button>
                            <Button data-testid="focus-play-pause-button" size="icon" className="h-11 w-11 rounded-full" onClick={togglePlay}>
                                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                            </Button>
                            <Button data-testid="focus-next-button" variant="ghost" size="icon" className="text-t2" onClick={next}><ChevronDown className="h-5 w-5" /></Button>
                        </div>
                        <div className="flex items-center gap-3">
                            <Gauge className="h-4 w-4 text-t4" />
                            <div className="w-28"><Slider data-testid="focus-wpm-slider" value={[wpm]} min={100} max={1500} step={25} onValueChange={(v) => setWpm(v[0])} /></div>
                            <span className="w-16 font-mono-ui text-xs text-t2">{wpm} wpm</span>
                        </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between font-mono-ui text-[11px] text-t4">
                        <span>{pct}%</span>
                        <span>{t("rset.grain.sentence")} {Math.min(active + 1, count)} / {count}</span>
                        <span className="hidden sm:inline">Space · ↑ ↓ · F</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
